/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getJob, removeDownloadJob} from '@ion/core/src/services/Connect';
import {CreateJob, DownloadJobType} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseCancelJobResult = UseDefaultQueryResult & {
  cancelJob: (job: CreateJob) => void;
}

const api = async(job: CreateJob) => {
  try {
    const data          = await getJob(job?.jobId);
    data.progressStatus = DownloadJobType.Cancelling;
    const result        = await removeDownloadJob(data);
    return result;
  } catch (err) {
    error(err);
    throw new Error(`An ${err.status || ''} Error Occurred`);
  }
};

/**
 * Common hook for cancelling a Job entity.
 *
 * ```typescript
 *
 *    import useCancelJob from '@ion/query/src/hooks/';
 *
 *    const {resendShipment, isSuccess, isError} = useCancelJob();
 *
 * ```
 */
export const useCancelJob = (): UseCancelJobResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.CANCEL_JOB]);
    }
  });

  const cancelJob = useCallback((job: CreateJob) => {
    mutation.mutate(job);
  }, [mutation]);

  return createMutationResult(
    mutation, {cancelJob}
  ) as UseCancelJobResult;
};

export default useCancelJob;
